/**
 * Client-side mirror of assertCanInvite (`apps/api/src/lib/invite-permissions.ts`).
 * Drives the invite button's visibility, the role picker's options, and the
 * store picker's source. The server STILL enforces the same matrix; this
 * just keeps the UI honest about what'll succeed.
 */
import { useAuth } from "../../providers/auth-provider.js";
import type { UserRole } from "../api/users.js";

export interface InvitePolicy {
  /** Whether the caller can invite at all (drives the button visibility). */
  canInvite: boolean;
  /** Roles the caller may pick as the invitee's role. */
  allowedRoles: ReadonlyArray<UserRole>;
  /**
   * "all"    -> store-scoped invitees can be bound to any tenant store
   *             (tenant_admin caller).
   * "scoped" -> only `allowedStoreIds` are selectable (store_manager caller).
   */
  storeMode: "all" | "scoped";
  /** Populated only when storeMode === "scoped"; the manager's accessible stores. */
  allowedStoreIds: ReadonlyArray<string>;
}

const NONE: InvitePolicy = {
  canInvite: false,
  allowedRoles: [],
  storeMode: "all",
  allowedStoreIds: [],
};

const TENANT_ADMIN_POLICY: InvitePolicy = {
  canInvite: true,
  // Only a tenant_admin can invite a tenant_admin (mirrors assertCanInvite).
  allowedRoles: [
    "tenant_admin",
    "ops_manager",
    "store_manager",
    "store_associate",
    "field_auditor",
    "viewer",
  ],
  storeMode: "all",
  allowedStoreIds: [],
};

export function useInvitePolicy(): InvitePolicy {
  const { user } = useAuth();
  if (!user || !user.role) return NONE;

  if (user.role === "tenant_admin") return TENANT_ADMIN_POLICY;

  if (user.role === "store_manager") {
    // The accessibleStores field tells us which stores the manager controls.
    // tenant-wide sentinel ("all") is unreachable for store_manager but we
    // type-narrow defensively.
    const access = user.accessibleStores;
    if (!access || access.mode === "all") {
      return {
        canInvite: true,
        allowedRoles: ["store_associate", "field_auditor"],
        storeMode: "all",
        allowedStoreIds: [],
      };
    }
    // No store bindings => can technically log in but can't invite anyone
    // anywhere; hide the button so the affordance doesn't lie.
    if (access.storeIds.length === 0) return NONE;
    return {
      canInvite: true,
      allowedRoles: ["store_associate", "field_auditor"],
      storeMode: "scoped",
      allowedStoreIds: access.storeIds,
    };
  }

  return NONE;
}
